export type NullableBoolean = boolean | null;

export interface QaAnswer {
  domain?: string;
  block?: string;
  key?: string;
  criterion_key: string;
  criterion_label_es?: string;
  question_es?: string;
  answer: NullableBoolean;
  justification?: string;
  confianza?: number | null;
  evidencia?: string;
  applicable?: boolean;
  not_applicable?: boolean;
  expected_answer?: NullableBoolean;
  pass: NullableBoolean;
  result_usage?: string;
  weight_points?: number;
  score_awarded?: number;
  score_possible?: number;
  severity_if_failed?: string | null;
}

export interface QaImportRow {
  evaluation_id?: string;
  conversation_id: string;
  agent_id?: string | null;
  agent_name?: string | null;
  ruleset_version?: string | null;
  score_awarded?: number | null;
  score_possible?: number | null;
  score_percentage?: number | null;
  general_score_awarded?: number | null;
  general_score_possible?: number | null;
  general_score_percentage?: number | null;
  total_questions?: number | null;
  passed_questions?: number | null;
  failed_questions?: number | null;
  not_applicable_questions?: number | null;
  resumen_general_qa?: string | null;
  answers_json?: string | QaAnswer[] | null;
  general_answers_json?: string | QaAnswer[] | null;
  failed_criteria_json?: string | QaAnswer[] | null;
  not_applicable_criteria_json?: string | QaAnswer[] | null;
  evaluated_at: string;
  source_row?: number | null;
  source_file?: string | null;
}

export interface QaStoredEvaluation {
  evaluation_id: string;
  conversation_id: string;
  agent_id: string | null;
  agent_name: string;
  ruleset_version: string;
  score_awarded: number;
  score_possible: number;
  score_percentage: number | null;
  general_score_awarded: number;
  general_score_possible: number;
  general_score_percentage: number | null;
  total_questions: number;
  passed_questions: number;
  failed_questions: number;
  not_applicable_questions: number;
  resumen_general_qa: string;
  answers_json: string;
  general_answers_json: string;
  failed_criteria_json: string;
  not_applicable_criteria_json: string;
  evaluated_at: string;
  evaluated_month: string;
  source_row: number | null;
  source_file: string | null;
  imported_at: string;
}

export interface QaAgentSummary {
  agent_name: string;
  evaluations: number;
  avg_score: number | null;
  min_score: number | null;
  max_score: number | null;
  failed_evaluations: number;
  clean_evaluations: number;
  clean_rate: number | null;
  failed_criteria: number;
  not_applicable_questions: number;
  low_score_evaluations: number;
}

export interface QaCriterionSummary {
  criterion_key: string;
  criterion_label: string;
  domain: string;
  evaluated_answers: number;
  passed_answers: number;
  failed_answers: number;
  not_applicable_answers: number;
  failure_rate: number | null;
}

export interface QaSummary {
  ok: true;
  month: string;
  generated_at: string;
  agent_filter: string | null;
  sample: {
    stored_rows: number;
    unique_conversations: number;
    deduplicated_rows: number;
    available_agents: number;
  };
  totals: {
    evaluations: number;
    avg_score: number | null;
    min_score: number | null;
    max_score: number | null;
    clean_evaluations: number;
    clean_rate: number | null;
    failed_evaluations: number;
    failed_criteria: number;
    not_applicable_questions: number;
    low_score_evaluations: number;
  };
  agents: QaAgentSummary[];
  criteria: QaCriterionSummary[];
  available_agents: string[];
}

export interface QaDrillRow {
  evaluation_id: string;
  conversation_id: string;
  agent_name: string;
  ruleset_version: string;
  evaluated_at: string;
  score_percentage: number | null;
  failed_questions: number;
  not_applicable_questions: number;
  resumen_general_qa: string;
  failed_criteria: Array<{
    criterion_key: string;
    criterion_label_es?: string;
    severity_if_failed?: string | null;
    justification?: string;
    evidencia?: string;
  }>;
  answers: QaAnswer[];
}

export interface QaDrillResponse {
  ok: true;
  month: string;
  agent_filter: string | null;
  page: number;
  page_size: number;
  total: number;
  rows: QaDrillRow[];
}

export interface QaMonthsResponse {
  ok: true;
  months: Array<{ month: string; evaluations: number; unique_conversations: number }>;
  latest_month: string | null;
}

export interface ApiErrorBody {
  ok: false;
  error: string;
  message?: string;
}

export function displayAgentName(value: string | null | undefined): string {
  const normalized = value?.trim();
  if (!normalized) return "Sin agente";
  return normalized.replace(/[._]+/g, " ").replace(/\s+/g, " ").trim();
}

export function roundMetric(value: number | null, decimals = 1): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}
